import { useQuery, useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { CurrentUserActiveFirmQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserActiveFirmResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CircularProgress } from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import Backdrop from '@material-ui/core/Backdrop'
import IconButton from '@material-ui/core/IconButton'
import Slide from '@material-ui/core/Slide'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CloseIcon from '@material-ui/icons/Close'
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined'
import MapOutlinedIcon from '@material-ui/icons/MapOutlined'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setShowBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import DelaHeaderHOC from 'components/AskDelaChat/DelaHeaderHOC'
import { processEnv } from 'components/configuration/environmentVariables'
import { baseProjectImageUrl } from 'pages/ProjectsPage/components/projectImageUtil'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { NavLink, useLocation } from 'react-router-dom'
import DeltekIcon from 'resources/DeltekIcon'
import DefaultActionBar from './DefaultActionBar'
import FullNavBarMenu from './FullNavBarMenu'
import HiddenOnBreakpoint from './HiddenOnBreakpoint'
import NavBarMenu from './NavBarMenu'
import ResponsiveBreakpointContainer from './ResponsiveBreakpointContainer'

const appHeaderHeight = 60

interface StyleProps {
    isStudioIconLoaded: boolean
}

const useAppHeaderStyles = makeStyles((theme: Theme) => ({
    fullHeight: {
        height: appHeaderHeight,
    },
    appHeaderContainer: {
        zIndex: theme.zIndex.appBar + 1,
        position: 'relative',
    },
    appBarContainer: {
        zIndex: theme.zIndex.appBar + 1,
        background: `linear-gradient(90.06deg, #2F4858 49.48%, #5F788A 100%)`,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'row',
    },
    actionBar: {
        zIndex: theme.zIndex.appBar - 1,
        background: theme.palette.grey[700],
        transition: '100ms',
    },
    whiteFont: {
        color: theme.palette.common.white,
    },
    iconButton: {
        borderRadius: 0,
        opacity: 0.45,
        width: appHeaderHeight,
        transition: '200ms',

        '&:hover': {
            opacity: 1,
        },
    },
    reset: {
        padding: 0,
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    iconButtonActive: {
        backgroundColor: `rgba(0,0,0,0.25)`,
        opacity: 1,

        '&:hover': {
            backgroundColor: `rgba(0,0,0,0.25)`,
        },
    },
    hasDivider: {
        '&:after,&:before': {
            content: '""',
            width: 1,
            height: appHeaderHeight,
            backgroundColor: 'rgba(255,255,255,0.25)',

            '&:hover': {
                backgroundColor: theme.palette.common.white,
            },
        },
    },
    appHeaderLeftContentContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100%',
        maxWidth: pxToRem(580),
        [theme.breakpoints.down('xs')]: {
            width: 'unset',
        },
    },
    fixedWidthContent: {
        width: 330,
    },
    appHeaderContentContainer: {
        height: '100%',
        paddingLeft: theme.spacing(2.6),
        flexWrap: 'nowrap',
    },
    fullPageMenuContainer: {
        zIndex: theme.zIndex.appBar,
        top: appHeaderHeight,
    },
    backdrop: {
        zIndex: theme.zIndex.appBar,
        backgroundColor: 'rgba(0,0,0,0.85)',
    },
    smallScreenWarningText: {
        color: theme.palette.common.white,
        textAlign: 'center',
    },
    warningCloseButton: {
        position: 'absolute',
        top: appHeaderHeight,
        right: 0,
    },
    deltekIcon: {
        '&.MuiSvgIcon-root': {
            width: pxToRem(230),
            fontSize: pxToRem(80),
        },
    },
    logoContainer: {
        paddingRight: theme.spacing(7),
        paddingLeft: theme.spacing(1),
        '@media (max-width: 1024px)': {
            paddingRight: theme.spacing(3),
        },
    },
    studioLoading: {
        padding: theme.spacing(2),
        '& .MuiCircularProgress-root': {
            color: '#b1abab',
        },
    },
    studioIcon: {
        display: (props: StyleProps) =>
            props.isStudioIconLoaded ? 'block' : 'none',
    },
    flex: {
        display: 'flex',
    },
}))

export interface AppHeaderProps {
    /**
     * Defines the content for the right-hand side of the nav bar.
     */
    rightSideContent?: React.ReactNode

    /**
     * Defines the content for the middle portion of the nav bar.
     */
    middleContent?: React.ReactNode | null

    /**
     * Shows/hides the action bar.
     */
    hideActionBar?: boolean

    /**
     * This component is used in place of the default action bar
     * if a non-null value is provided.
     */
    customActionBar?: React.ReactNode | null

    /**
     * Handler for when the height of the header changes,
     * for example, for responsive design reasons.
     *
     * This is primarily used to adjust the height of
     * the top-level content container.
     */
    onHeightChange?: (newHeight: number) => void

    /**
     * Title text used by the default action bar.
     * If a customActionBar is used, this value is unused.
     */
    pageTitle?: string

    /**
     * Menu items for the help menu dropdown.
     * This is a copy for the responsive full
     * page menu.
     */
    helpMenuItems?: MenuItemConfig[]

    /**
     * Menu items for the user menu dropdown.
     * This is a copy for the responsive full
     * page menu.
     */
    userMenuItems?: MenuItemConfig[]

    customMenuItems?: MenuItemConfig[] | React.ReactNode | null
    customPartialNavBarMenuComponent?: React.ReactNode | null

    /**
     * If visible, true
     */
    headerVisible?: boolean
}

const AppHeader: React.FC<AppHeaderProps> = ({
    pageTitle = '',
    rightSideContent,
    middleContent = null,
    hideActionBar = true,
    customActionBar = null,
    onHeightChange,
    helpMenuItems = [],
    userMenuItems = [],
    customMenuItems = null,
    customPartialNavBarMenuComponent = null,
    headerVisible = true,
}) => {
    const { pathname } = useLocation()

    // remove this when SMM is production ready
    const isSMMEnabled = processEnv.REACT_APP_ShowSMM === '1'

    const [isMenuOpen, setIsMenuOpen] = React.useState(false)
    const [isStudioIconLoaded, setIsStudioIconLoaded] = React.useState(false)
    const theme = useTheme()
    const xsDown = useMediaQuery(theme.breakpoints.down('xs'))
    const [warningOpen, setWarningOpen] = React.useState(true)

    const { isSystemUser } = useReactiveVar(userInfo)
    const { setUserActiveFirm } = React.useContext(AppBarContext)

    const classes = useAppHeaderStyles({
        isStudioIconLoaded,
    })

    const isStudioOrWorkItemPage =
        pathname.startsWith('/master') || pathname.startsWith('/workflow')

    const showHeaderDelaIcon =
        (pathname.includes('/projects') || isStudioOrWorkItemPage) &&
        process.env.REACT_APP_ShowSpecpointCopilot === '1'

    /* Close the menu on page change
     */
    React.useEffect(() => {
        if (xsDown) {
            setIsMenuOpen(false)
        }
    }, [pathname, xsDown])

    const closeWarningOverlay = () => {
        setWarningOpen(false)
    }

    const handleHeightChange = (_: number, height: number) => {
        onHeightChange?.(height)
    }

    const handleMenuOpenClose = () => {
        setIsMenuOpen(!isMenuOpen)
    }

    useQuery<CurrentUserActiveFirmResponse>(CurrentUserActiveFirmQuery, {
        onCompleted: (data) => {
            setUserActiveFirm?.(data?.myActiveFirm)

            // Using the user's firm details, set if best practice setting should be shown
            setShowBestPracticeSetting(data?.myActiveFirm)
        },
    })

    const isStudioVisible = React.useMemo(
        () => !isSystemUser || (isSystemUser && isSMMEnabled),
        [isSystemUser]
    )

    const handleStudioImageLoad = React.useCallback(
        () => setIsStudioIconLoaded(true),
        [isStudioIconLoaded]
    )

    const loader = React.useMemo(
        () => (
            <div
                className={classes.studioLoading}
                data-testid="studioIconloader"
            >
                <CircularProgress aria-label="Loading" size={20} />
            </div>
        ),
        []
    )

    return (
        <GridContainer
            data-testid="appHeader"
            direction="column"
            className={classes.appHeaderContainer}
        >
            {headerVisible && (
                <>
                    <ReactResizeDetector
                        handleHeight={true}
                        handleWidth={false}
                        onResize={handleHeightChange}
                    />
                    <GridItem container>
                        <AppBar
                            position="relative"
                            className={clsx(
                                'appHeader',
                                classes.appBarContainer,
                                classes.fullHeight
                            )}
                        >
                            <ContainerMaxWidthXl>
                                <GridContainer
                                    alignItems="flex-start"
                                    className={
                                        classes.appHeaderContentContainer
                                    }
                                    justifyContent="space-between"
                                >
                                    <div
                                        className={clsx(
                                            classes.appHeaderLeftContentContainer
                                        )}
                                    >
                                        <GridContainer
                                            alignItems="center"
                                            spacing={1}
                                            className={classes.logoContainer}
                                        >
                                            <DeltekIcon
                                                className={classes.deltekIcon}
                                            ></DeltekIcon>
                                        </GridContainer>
                                        {!pathname.includes(
                                            '/contacts/details/'
                                        ) && (
                                            <HiddenOnBreakpoint>
                                                <div>
                                                    <NavLink to="/projects">
                                                        <IconButton
                                                            data-testid="buttonHome"
                                                            aria-label="Home Button"
                                                            disableRipple
                                                            disableFocusRipple
                                                            disableTouchRipple
                                                            className={clsx(
                                                                classes.iconButton,
                                                                classes.hasDivider,
                                                                classes.whiteFont,
                                                                classes.reset,
                                                                pathname.startsWith(
                                                                    '/projects/home'
                                                                ) &&
                                                                    classes.iconButtonActive
                                                            )}
                                                        >
                                                            <HomeOutlinedIcon />
                                                        </IconButton>
                                                    </NavLink>
                                                </div>
                                                <div>
                                                    <NavLink to="/dashboard">
                                                        <IconButton
                                                            data-testid="buttonMaps"
                                                            aria-label="Maps Button"
                                                            disableRipple
                                                            disableFocusRipple
                                                            disableTouchRipple
                                                            className={clsx(
                                                                classes.iconButton,
                                                                classes.whiteFont,
                                                                classes.hasDivider,
                                                                classes.reset,
                                                                pathname.startsWith(
                                                                    '/dashboard'
                                                                ) &&
                                                                    classes.iconButtonActive
                                                            )}
                                                        >
                                                            <MapOutlinedIcon />
                                                        </IconButton>
                                                    </NavLink>
                                                </div>

                                                {isStudioVisible && (
                                                    <div>
                                                        <NavLink to="/master">
                                                            <IconButton
                                                                data-testid="buttonStudio"
                                                                aria-label="Studio Button"
                                                                disableRipple
                                                                disableFocusRipple
                                                                disableTouchRipple
                                                                className={clsx(
                                                                    classes.iconButton,
                                                                    classes.whiteFont,
                                                                    classes.hasDivider,
                                                                    classes.reset,
                                                                    isStudioOrWorkItemPage &&
                                                                        classes.iconButtonActive
                                                                )}
                                                            >
                                                                {!isStudioIconLoaded &&
                                                                    loader}
                                                                <img
                                                                    className={
                                                                        classes.studioIcon
                                                                    }
                                                                    alt="studio"
                                                                    src={`${baseProjectImageUrl}/studio.svg`}
                                                                    onLoad={
                                                                        handleStudioImageLoad
                                                                    }
                                                                />
                                                            </IconButton>
                                                        </NavLink>
                                                    </div>
                                                )}
                                            </HiddenOnBreakpoint>
                                        )}
                                    </div>
                                    {!pathname.includes(
                                        '/contacts/details/'
                                    ) && (
                                        <ResponsiveBreakpointContainer
                                            xs={
                                                <NavBarMenu
                                                    isActive={isMenuOpen}
                                                    onClick={
                                                        handleMenuOpenClose
                                                    }
                                                />
                                            }
                                            sm={
                                                middleContent && (
                                                    <NavBarMenu
                                                        isActive={isMenuOpen}
                                                        onClick={
                                                            handleMenuOpenClose
                                                        }
                                                    />
                                                )
                                            }
                                            custom={
                                                middleContent && (
                                                    <NavBarMenu
                                                        isActive={isMenuOpen}
                                                        onClick={
                                                            handleMenuOpenClose
                                                        }
                                                    />
                                                )
                                            }
                                            lg={
                                                middleContent && (
                                                    <GridItem
                                                        container
                                                        xs={true}
                                                        alignItems="center"
                                                        className={
                                                            classes.fullHeight
                                                        }
                                                    >
                                                        {middleContent}
                                                    </GridItem>
                                                )
                                            }
                                        />
                                    )}

                                    {!pathname.includes(
                                        '/contacts/details/'
                                    ) && (
                                        <HiddenOnBreakpoint breakpoint="xs">
                                            <div
                                                className={clsx(
                                                    classes.fullHeight,
                                                    showHeaderDelaIcon &&
                                                        classes.flex
                                                )}
                                            >
                                                {rightSideContent}
                                                {showHeaderDelaIcon && (
                                                    <DelaHeaderHOC />
                                                )}
                                            </div>
                                        </HiddenOnBreakpoint>
                                    )}
                                </GridContainer>
                            </ContainerMaxWidthXl>
                        </AppBar>
                    </GridItem>

                    <ResponsiveBreakpointContainer
                        xs={
                            <Slide in={isMenuOpen} mountOnEnter unmountOnExit>
                                <div
                                    className={clsx(
                                        classes.fullPageMenuContainer
                                    )}
                                >
                                    <FullNavBarMenu
                                        customContent={customMenuItems}
                                        helpMenuItems={helpMenuItems}
                                        userMenuItems={userMenuItems}
                                    />
                                </div>
                            </Slide>
                        }
                        sm={isMenuOpen && customPartialNavBarMenuComponent}
                        custom={isMenuOpen && customPartialNavBarMenuComponent}
                        lg={null}
                    />
                    {!hideActionBar && (
                        <GridItem
                            className={clsx('actionBar', classes.actionBar)}
                        >
                            {!customActionBar ? (
                                <DefaultActionBar pageTitle={pageTitle} />
                            ) : (
                                customActionBar
                            )}
                        </GridItem>
                    )}
                </>
            )}

            <Backdrop
                data-testid="smallScreenWarningBackdrop"
                open={xsDown && warningOpen}
                style={{
                    zIndex: theme.zIndex.appBar,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                }}
            >
                <IconButton
                    data-testid="smallScreenWarningCloseButton"
                    className={clsx(
                        classes.whiteFont,
                        classes.warningCloseButton
                    )}
                    onClick={closeWarningOverlay}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>
                <Typography
                    variant="body2"
                    className={classes.smallScreenWarningText}
                >
                    This website is optimized for tablet and desktop.
                    <br />
                    Please view on a bigger screen.
                </Typography>
            </Backdrop>
        </GridContainer>
    )
}

export default AppHeader
